/*
 * Content Publishing - New Database-Driven Architecture
 * 
 * How it works:
 * 1. User selects agent groups and media group, then clicks Publish
 * 2. System creates a publishing task in the database (not direct device updates)
 * 3. Publishing task contains: compID, agentGroupIDs, mediaGroupID, timestamp
 * 4. Digital signage devices poll the database every minute for new assignments
 * 5. When devices see new tasks, they download and display the new content
 * 6. Device status is tracked in publishing_status table
 * 7. Frontend shows real-time progress of publishing tasks
 * 
 * Benefits:
 * - Works even when devices are offline (they get updates when back online)
 * - No 404 errors from trying to contact devices directly
 * - Better tracking and monitoring of content distribution
 * - Supports scheduled publishing for future deployment
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { agentGroupsAPI, mediaGroupsAPI, agentsAPI, publishingAPI } from '../services/api';
import { FiSend, FiCheck, FiAlertCircle, FiUsers, FiMonitor, FiFileText, FiWifi, FiWifiOff } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const ContentPublishing = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  
  // Data states
  const [agentGroups, setAgentGroups] = useState([]);
  const [mediaGroups, setMediaGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Publishing states
  const [selectedAgentGroups, setSelectedAgentGroups] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedMediaGroup, setSelectedMediaGroup] = useState('');
  const [publishingResults, setPublishingResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState('groups'); // 'groups' or 'devices'

  // Add search/filter functionality
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [mediaSearchTerm, setMediaSearchTerm] = useState('');
  const [filterOnlineOnly, setFilterOnlineOnly] = useState(false);
  
  // Add scheduling states
  const [schedulePublishing, setSchedulePublishing] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // Add preview functionality
  const [showPreview, setShowPreview] = useState(false);

  // Add connection check functionality
  const [connectionStatus, setConnectionStatus] = useState('unknown');
  const [showConnectionCheck, setShowConnectionCheck] = useState(false);
  
  useEffect(() => {
    if (user?.compID) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const checkDatabaseConnection = async () => {
    setConnectionStatus('checking');
    setShowConnectionCheck(true);
    try {
      // Simple connection test by fetching agent groups
      await agentGroupsAPI.getAgentGroups(user.compID);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Connection check failed:', error);
      setConnectionStatus('disconnected');
    }
  };

  const fetchData = async () => {
    try {
      const [agentGroupsData, mediaGroupsData, agentsData] = await Promise.all([
        agentGroupsAPI.getAgentGroups(user.compID),
        mediaGroupsAPI.getMediaGroups(user.compID),
        agentsAPI.getAgents(user.compID)
      ]);
      
      setAgentGroups(agentGroupsData);
      setMediaGroups(mediaGroupsData);
      setAgents(agentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentGroupToggle = (groupId) => {
    setSelectedAgentGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleAgentToggle = (agentId) => {
    setSelectedAgents(prev => 
      prev.includes(agentId) 
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const getAgentsInGroups = () => {
    if (selectionMode === 'devices') {
      return agents.filter(agent => selectedAgents.includes(agent.agentID) && agent.agentActive);
    }
    return agents.filter(agent => 
      selectedAgentGroups.includes(agent.agentGroupsID) && agent.agentActive
    );
  };

  const getSelectedMediaGroupDetails = () => {
    return mediaGroups.find(group => group.mediaGroupID === parseInt(selectedMediaGroup));
  };

  // Utility functions
  const isAgentOnline = (lastOnline) => {
    if (!lastOnline) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastOnline) > fiveMinutesAgo;
  };

  const getAgentGroupName = (agentGroupID) => {
    const group = agentGroups.find(g => g.agentGroupsID === agentGroupID);
    return group ? group.agentGroupName : 'Unknown Group';
  };

  // Search and filter functionality
  const filteredAgentGroups = agentGroups.filter(group =>
    group.agentGroupName.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(agentSearchTerm.toLowerCase())
  );

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.agentName.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
      agent.agentSerial?.toLowerCase().includes(agentSearchTerm.toLowerCase());
    const matchesOnlineFilter = !filterOnlineOnly || isAgentOnline(agent.agentLastOnline);
    return matchesSearch && matchesOnlineFilter && agent.agentActive;
  });

  const filteredMediaGroups = mediaGroups.filter(group =>
    group.mediaGroupName.toLowerCase().includes(mediaSearchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(mediaSearchTerm.toLowerCase())
  );

  // Scheduled publishing (placeholder for future implementation)
  const handleScheduledPublish = () => {
    toast('Scheduled publishing feature coming soon!');
  };

  const handlePublishContent = async () => {
    if (!selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)) {
      toast.error(`Please select at least one ${selectionMode === 'groups' ? 'agent group' : 'device'} and a media group`);
      return;
    }

    setPublishing(true);
    
    try {
      const affectedAgents = getAgentsInGroups();
      
      if (affectedAgents.length === 0) {
        toast.error('No active agents found in selected groups');
        return;
      }
      
      const mediaGroupID = parseInt(selectedMediaGroup);
      const scheduledDateTime = schedulePublishing && scheduledDate && scheduledTime 
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : null;
      
      console.log('Creating publishing task:', {
        compID: user.compID,
        mode: selectionMode,
        targetIDs: selectionMode === 'groups' ? selectedAgentGroups : selectedAgents,
        mediaGroupID,
        scheduledTime: scheduledDateTime,
        affectedAgentsCount: affectedAgents.length
      });

      // Create publishing task using the dedicated endpoint for the selection mode
      let task;
      if (selectionMode === 'groups') {
        task = await publishingAPI.createPublishingTaskByGroup(
          user.compID,
          selectedAgentGroups,
          mediaGroupID,
          scheduledDateTime
        );
      } else {
        task = await publishingAPI.createPublishingTaskByAgent(
          user.compID,
          selectedAgents,
          mediaGroupID,
          scheduledDateTime
        );
      }
      
      console.log('Publishing task created:', task);
      
      // Show success message
      const results = [{
        status: 'success',
        message: `Publishing task created successfully! Task ID: ${task.taskID || 'N/A'}`,
        details: `${affectedAgents.length} devices will receive the update when they check for new content. View progress in Publishing Tasks page.`
      }];
      
      setPublishingResults(results);
      setShowResults(true);
      
      // Reset form
      setSelectedAgentGroups([]);
      setSelectedAgents([]);
      setSelectedMediaGroup('');
      setSchedulePublishing(false);
      setScheduledDate('');
      setScheduledTime('');

    } catch (error) {
      console.error('Error creating publishing task:', error);
      
      let errorMessage = 'Failed to create publishing task. ';
      
      if (error.response) {
        errorMessage += `Server error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`;
      } else if (error.request) {
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        errorMessage += error.message;
      }
      
      const results = [{
        status: 'error',
        message: 'Failed to create publishing task',
        details: errorMessage
      }];
      
      setPublishingResults(results);
      setShowResults(true);
    } finally {
      setPublishing(false);
    }
  };

  // Fallback method for individual agent updates
  const handlePublishContentFallback = async () => {
    if (!selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)) {
      toast.error(`Please select at least one ${selectionMode === 'groups' ? 'agent group' : 'device'} and a media group`);
      return;
    }

    setPublishing(true);
    const results = [];
    
    try {
      const affectedAgents = getAgentsInGroups();
      const mediaGroupID = parseInt(selectedMediaGroup);
      
      console.log('Using fallback method for individual updates');
      
      // Update agents one by one
      for (const agent of affectedAgents) {
        try {
          await agentsAPI.updateAgent(agent.agentID, {
            ...agent,
            compID: user.compID,
            mediaGroupID: mediaGroupID
          });
          
          results.push({
            agentID: agent.agentID,
            agentName: agent.agentName,
            status: 'success',
            message: 'Successfully updated'
          });
        } catch (agentError) {
          console.error(`Error updating agent ${agent.agentName}:`, agentError);
          results.push({
            agentID: agent.agentID,
            agentName: agent.agentName,
            status: 'error',
            message: agentError.response?.data?.message || 'Failed to update'
          });
        }
      }

      setPublishingResults(results);
      setShowResults(true);
      
      // Refresh agents data
      const updatedAgents = await agentsAPI.getAgents(user.compID);
      setAgents(updatedAgents);

    } catch (error) {
      console.error('Error in bulk publishing fallback:', error);
      // Handle error appropriately
      toast.error('Failed to publish content. Please check your database connection.');
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (user?.compID) {
      fetchData();
    }
  }, [user?.compID]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content publishing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Publishing</h1>
          <p className="mt-2 text-gray-600">
            Distribute content to your devices by publishing media groups to agent groups
          </p>
        </div>
        
        {/* Connection Status Indicator */}
        <div className="flex items-center space-x-3">
          {showConnectionCheck && (
            <div className="flex items-center space-x-2">
              {connectionStatus === 'checking' && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              )}
              {connectionStatus === 'connected' && (
                <FiWifi className="h-4 w-4 text-green-500" />
              )}
              {connectionStatus === 'disconnected' && (
                <FiWifiOff className="h-4 w-4 text-red-500" />
              )}
              <span className={`text-sm ${
                connectionStatus === 'connected' 
                  ? 'text-green-600' 
                  : connectionStatus === 'disconnected'
                  ? 'text-red-600'
                  : 'text-gray-600'
              }`}>
                {connectionStatus === 'checking' 
                  ? 'Checking connection...'
                  : connectionStatus === 'connected'
                  ? 'Database Connected'
                  : connectionStatus === 'disconnected'
                  ? 'Database Disconnected'
                  : 'Connection Unknown'
                }
              </span>
            </div>
          )}
          
          <button
            onClick={checkDatabaseConnection}
            disabled={connectionStatus === 'checking'}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center space-x-1 ${
              connectionStatus === 'checking'
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            <FiWifi className="h-3 w-3" />
            <span>Test Connection</span>
          </button>
        </div>
      </div>

      {/* Main Content Grid - Horizontal Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Selection Mode and Target Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          {/* Selection Mode Radio Buttons */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Selection Mode</h3>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="selectionMode"
                  value="groups"
                  checked={selectionMode === 'groups'}
                  onChange={(e) => {
                    setSelectionMode(e.target.value);
                    setSelectedAgents([]);
                    setSelectedAgentGroups([]);
                    setAgentSearchTerm('');
                  }}
                  className="text-blue-600"
                />
                <FiUsers className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Agent Groups</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="selectionMode"
                  value="devices"
                  checked={selectionMode === 'devices'}
                  onChange={(e) => {
                    setSelectionMode(e.target.value);
                    setSelectedAgents([]);
                    setSelectedAgentGroups([]);
                    setAgentSearchTerm('');
                  }}
                  className="text-blue-600"
                />
                <FiMonitor className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Individual Devices</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              {selectionMode === 'groups' ? (
                <>
                  <FiUsers className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Agent Groups</h3>
                </>
              ) : (
                <>
                  <FiMonitor className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Individual Devices</h3>
                </>
              )}
            </div>
          </div>
          
          {/* Search and Filter */}
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="text"
              placeholder={`Search ${selectionMode === 'groups' ? 'agent groups' : 'devices'}...`}
              value={agentSearchTerm}
              onChange={(e) => setAgentSearchTerm(e.target.value)}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
            />
            {selectionMode === 'devices' && (
              <button
                onClick={() => setFilterOnlineOnly(prev => !prev)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  filterOnlineOnly
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filterOnlineOnly ? 'Show All' : 'Online Only'}
              </button>
            )}
          </div>
          
          {/* Agent Groups List */}
          {selectionMode === 'groups' && (
            <>
              {filteredAgentGroups.length === 0 ? (
                <p className="text-gray-500 text-sm">No agent groups found</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredAgentGroups.map(group => {
                    const groupAgents = agents.filter(agent => agent.agentGroupsID === group.agentGroupsID);
                    const activeAgents = groupAgents.filter(agent => agent.agentActive);
                    const onlineAgents = activeAgents.filter(agent => isAgentOnline(agent.agentLastOnline));
                    
                    return (
                      <div
                        key={group.agentGroupsID}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          selectedAgentGroups.includes(group.agentGroupsID)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleAgentGroupToggle(group.agentGroupsID)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{group.agentGroupName}</h4>
                            <p className="text-sm text-gray-500">{group.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {onlineAgents.length}/{activeAgents.length}
                            </div>
                            <div className="text-xs text-gray-500">Online/Active</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Individual Devices List */}
          {selectionMode === 'devices' && (
            <>
              {filteredAgents.length === 0 ? (
                <p className="text-gray-500 text-sm">No devices found</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredAgents.map(agent => (
                    <div
                      key={agent.agentID}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                        selectedAgents.includes(agent.agentID)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleAgentToggle(agent.agentID)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{agent.agentName}</h4>
                          <p className="text-sm text-gray-500">Serial: {agent.agentSerial}</p>
                          <p className="text-sm text-gray-500">Group: {getAgentGroupName(agent.agentGroupsID)}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${
                            isAgentOnline(agent.agentLastOnline) ? 'bg-green-400' : 'bg-gray-400'
                          }`}></div>
                          <span className={`text-xs ${
                            isAgentOnline(agent.agentLastOnline) ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {isAgentOnline(agent.agentLastOnline) ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Media Group Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FiFileText className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Select Media Group</h3>
          </div>
          
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="text"
              placeholder="Search media groups..."
              value={mediaSearchTerm}
              onChange={(e) => setMediaSearchTerm(e.target.value)}
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
            />
          </div>
          
          {filteredMediaGroups.length === 0 ? (
            <p className="text-gray-500 text-sm">No media groups found</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredMediaGroups.map(group => (
                <div
                  key={group.mediaGroupID}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedMediaGroup === group.mediaGroupID.toString()
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedMediaGroup(group.mediaGroupID.toString())}
                >
                  <h4 className="font-medium text-gray-900">{group.mediaGroupName}</h4>
                  <p className="text-sm text-gray-500">{group.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Publishing Summary */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <FiMonitor className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Publishing Summary</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {selectionMode === 'groups' ? 'Selected Agent Groups' : 'Selected Devices'}
              </h4>
              {selectionMode === 'groups' ? (
                selectedAgentGroups.length === 0 ? (
                  <p className="text-sm text-gray-500">No groups selected</p>
                ) : (
                  <div className="space-y-1">
                    {selectedAgentGroups.map(groupId => (
                      <div key={groupId} className="text-sm text-gray-900">
                        • {getAgentGroupName(groupId)}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                selectedAgents.length === 0 ? (
                  <p className="text-sm text-gray-500">No devices selected</p>
                ) : (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedAgents.map(agentId => {
                      const agent = agents.find(a => a.agentID === agentId);
                      return (
                        <div key={agentId} className="text-sm text-gray-900">
                          • {agent?.agentName || `Agent ${agentId}`}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Media Group</h4>
              {selectedMediaGroup ? (
                <p className="text-sm text-gray-900">{getSelectedMediaGroupDetails()?.mediaGroupName}</p>
              ) : (
                <p className="text-sm text-gray-500">No media group selected</p>
              )}
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Affected Agents</h4>
              <div className="text-2xl font-bold text-blue-600">
                {getAgentsInGroups().length}
              </div>
              <p className="text-sm text-gray-500">agents will receive new content</p>
            </div>

            {/* Scheduling Option */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={schedulePublishing}
                    onChange={(e) => setSchedulePublishing(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Schedule Publishing</span>
                </label>
              </div>
              
              {schedulePublishing && (
                <div className="grid grid-cols-1 gap-2 mt-2">
                  <div>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => setShowPreview(true)}
                disabled={!selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  !selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
              >
                <span>Preview Distribution</span>
              </button>

              <button
                onClick={schedulePublishing ? handleScheduledPublish : handlePublishContent}
                disabled={publishing || !selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  publishing || !selectedMediaGroup || (selectionMode === 'groups' ? selectedAgentGroups.length === 0 : selectedAgents.length === 0)
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {publishing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Publishing...</span>
                  </>
                ) : (
                  <>
                    <FiSend className="h-4 w-4" />
                    <span>{schedulePublishing ? 'Schedule Publish' : 'Publish Now'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Affected Agents List */}
      {(selectedAgentGroups.length > 0 || selectedAgents.length > 0) && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {selectionMode === 'groups' ? 'Agents to be Updated (from selected groups)' : 'Selected Devices to be Updated'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getAgentsInGroups().map(agent => (
              <div key={agent.agentID} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{agent.agentName}</h4>
                  <div className={`w-3 h-3 rounded-full ${
                    isAgentOnline(agent.agentLastOnline) ? 'bg-green-400' : 'bg-gray-400'
                  }`}></div>
                </div>
                <p className="text-sm text-gray-500">Serial: {agent.agentSerial}</p>
                <p className="text-sm text-gray-500">Group: {getAgentGroupName(agent.agentGroupsID)}</p>
                <p className="text-sm text-gray-500">
                  Current Media: {agent.mediaGroupName || 'None assigned'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Publishing Results Modal */}
      {showResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Publishing Results</h3>
              <button
                onClick={() => setShowResults(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-3">
              {publishingResults.map((result, index) => (
                <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg ${
                  result.status === 'success' ? 'bg-green-50' : 'bg-red-50'
                }`}>
                  {result.status === 'success' ? (
                    <FiCheck className="h-5 w-5 text-green-600" />
                  ) : (
                    <FiAlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{result.agentName}</h4>
                    <p className={`text-sm ${
                      result.status === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {result.message}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Distribution Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Media Group Details</h4>
                {getSelectedMediaGroupDetails() && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h5 className="font-medium text-green-900">{getSelectedMediaGroupDetails().mediaGroupName}</h5>
                    <p className="text-sm text-green-700">{getSelectedMediaGroupDetails().description}</p>
                  </div>
                )}
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Distribution Summary</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">
                        {selectionMode === 'groups' ? 'Agent Groups:' : 'Selected Devices:'}
                      </span>
                      <span className="ml-2 text-blue-900">
                        {selectionMode === 'groups' ? selectedAgentGroups.length : selectedAgents.length}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Total Agents:</span>
                      <span className="ml-2 text-blue-900">{getAgentsInGroups().length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <h4 className="font-semibold text-gray-900 mb-2">Affected Agents</h4>
              <div className="max-h-40 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {getAgentsInGroups().map(agent => (
                    <div key={agent.agentID} className="bg-gray-50 p-2 rounded text-sm">
                      <div className="font-medium text-gray-900">{agent.agentName}</div>
                      <div className="text-gray-600">{getAgentGroupName(agent.agentGroupsID)}</div>
                      <div className={`text-xs ${isAgentOnline(agent.agentLastOnline) ? 'text-green-600' : 'text-red-600'}`}>
                        {isAgentOnline(agent.agentLastOnline) ? 'Online' : 'Offline'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  schedulePublishing ? handleScheduledPublish() : handlePublishContent();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <FiSend className="h-4 w-4" />
                <span>{schedulePublishing ? 'Schedule Publish' : 'Publish Now'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPublishing;
